/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { getSelf } from '@/lib/api'
import { appOrigin, isPublicHost } from '@/lib/host'
import { saveUserId } from '@/features/auth/lib/storage'
import { AuthenticatedLayout } from '@/components/layout'

// 内存中的验证标记，避免同一会话中重复验证
let sessionVerified = false

// 后台 console 只在 app 子域名 (api.<主域>) 运行；主域名仅前台。
// 若 console 被主域名加载,整体跳到 app 子域名,保证侧边栏所有相对链接都落在
// app 子域名,不会漏到主域名前台页面。跳转目标按当前域名派生
// (4allapi.com → api.4allapi.com, omniapi.co → api.omniapi.co)。

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    // Pin the whole console to the app subdomain.
    if (isPublicHost()) {
      throw redirect({
        href: `${appOrigin()}${window.location.pathname}${window.location.search}`,
      })
    }

    const { auth } = useAuthStore.getState()

    // 已有本地用户且本会话已验证过 → 直接放行
    if (auth.user && sessionVerified) return

    // 否则用会话 cookie（跨子域：SESSION_DOMAIN=.<父域>）调 getSelf() 恢复用户。
    // localStorage 用户缓存不跨 origin，但会话 cookie 跨子域有效，
    // 所以必须先 getSelf()，不能因本地无缓存就直接跳登录（否则主域登录后
    // 跳到 api 子域会被误判为未登录 → 二次登录）。
    const res = await getSelf().catch(() => null)
    if (res?.success && res.data) {
      // Persist uid (localStorage + cross-subdomain cookie) so later requests on
      // this origin carry New-Api-User directly.
      if (res.data.id != null) saveUserId(res.data.id)
      auth.setUser(res.data)
      sessionVerified = true
      return
    }

    // 会话无效：清除本地缓存并跳转登录页
    auth.reset()
    throw redirect({
      to: '/sign-in',
      search: { redirect: location.href },
    })
  },
  component: AuthenticatedLayout,
})
